import type { Tour } from '../../types'
import { target } from './target'

// Route guards: timesheet → can('timesheet','read'); invoicing/* → can('invoicing','read').
const INVOICING = { when: (a) => a.can('invoicing', 'read') } satisfies Tour['access']

export const financeTours: Tour[] = [
  {
    id: 'page:timesheet',
    version: 1,
    title: 'Timesheet',
    kind: 'page',
    match: /^\/timesheet\/?$/,
    access: { when: (a) => a.can('timesheet', 'read') },
    // Hours are typed in place: never a card, only the ? beacon.
    invite: 'beacon',
    steps: [
      { id: 'header-own', target: target('page-header'), access: { when: (a) => !a.can('invoicing', 'read') }, side: 'bottom', title: 'Timesheet', body: 'Your hours for the month.' },
      { id: 'header-all', target: target('page-header'), access: INVOICING, side: 'bottom', title: 'Timesheet', body: 'Everyone\'s logged and imported hours for the month: the hours invoice drafts bill from.' },
      // The month anchor exists only for a CODER now: an admin's month moved
      // into the filter drawer so the controls fit one line beside the
      // client's tabs (2026-10-27). Pointing an admin at a selector that is
      // not in the DOM would leave a step with nothing to attach to, so the
      // two audiences get the step they can actually see.
      { id: 'month', target: target('timesheet.month'), access: { when: (a) => !a.can('invoicing', 'read') }, side: 'bottom', title: 'Change month', body: 'Step back and forward a month.' },
      { id: 'filters', target: target('timesheet.filters'), access: INVOICING, side: 'bottom', title: 'Filters', body: 'Narrow the entries by client and facility — and change the month, which lives in here too.' },
    ],
  },
  {
    id: 'page:invoices',
    version: 1,
    title: 'Invoices',
    kind: 'page',
    match: /^\/invoicing(\/invoices)?\/?$/,
    access: INVOICING,
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Invoices', body: 'Drafts, approvals and outstanding balances.' },
      { id: 'filters', target: target('invoices.filters'), side: 'bottom', title: 'Filters', body: 'Narrow by client, facility, scope and billing period.' },
      { id: 'new', target: target('invoices.new'), access: { when: (a) => a.can('invoicing', 'edit') }, side: 'bottom', title: 'New invoice', body: 'Starts a draft. It is checked before approval and only then issued to the client.' },
    ],
  },
  {
    id: 'page:finance-dashboard',
    version: 1,
    title: 'Billing dashboard',
    kind: 'page',
    match: /^\/invoicing\/dashboard\/?$/,
    access: INVOICING,
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Billing dashboard', body: 'One client\'s billing at a glance: rate cards, invoices, contracts, and the issued-to-paid timeline.' },
      { id: 'client', target: target('finance.client'), side: 'bottom', title: 'Pick a client', body: 'Choose whose billing to look at.' },
    ],
  },
  {
    id: 'page:contracts-pricing',
    version: 1,
    title: 'Contracts & pricing',
    kind: 'page',
    match: /^\/invoicing\/contracts-pricing\/?$/,
    access: INVOICING,
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Contracts & pricing', body: 'What every client is charged, and what is still unpriced.' },
      { id: 'search', target: target('contracts.search'), side: 'bottom', title: 'Find a client', body: 'Rates are edited from that client\'s own record.' },
    ],
  },
  {
    id: 'page:invoice-detail',
    version: 2,
    title: 'Invoice',
    kind: 'page',
    match: /^\/invoicing\/invoices\/[^/]+\/?$/,
    access: INVOICING, // router: invoicing/*
    // Every edit on a draft writes immediately (inline lines): never navigate away.
    mayHoldEdits: true,
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'This invoice', body: 'The title shows the invoice number, or "Draft invoice", with its status beside it.' },
      { id: 'actions', target: target('invoice.actions'), access: { when: (a) => a.can('invoicing', 'edit') }, side: 'bottom', title: 'What you can do next', body: 'The actions this invoice allows right now, for example "Approve" on a draft, or "Record payment" and "Void invoice" once it is issued.' },
      { id: 'actions-read', target: target('invoice.actions'), access: { when: (a) => !a.can('invoicing', 'edit') }, side: 'bottom', title: 'Invoice actions', body: 'The actions open to you on this invoice appear here.' },
      { id: 'gate', target: target('invoice.gate'), whenMissing: 'Draft invoices list the checks they must pass before approval here. This invoice is past that stage.',  waitMs: 1500, side: 'bottom', title: 'Before approval', body: 'The checks a draft must pass before it can be approved. Any that fail are listed here.' },
      { id: 'lines', target: target('invoice.lines'), side: 'bottom', title: 'Lines', body: 'The invoice lines, grouped by what each one bills, with the total at the foot of the table.' },
      { id: 'preview', target: target('invoice.preview'), whenMissing: 'On a draft, "Show preview" in the header opens a live preview of the invoice document beside the lines. It is turned off, or this invoice is no longer a draft.',  waitMs: 1500, side: 'left', title: 'Preview', body: 'The invoice document as the client will receive it. "Download PDF" saves a copy; "Hide preview" folds it away.' },
    ],
  },
]
